import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

/**
 * Custom Mixpanel event names fired by the web app. These MUST match the
 * mobile app's event names exactly so web and mobile unify in one Mixpanel
 * project (web appears as a `web` value in the `mp_lib` breakdown). Do not
 * change these strings (including the historical `PLAY_GOLF_COURSE_STARTED`
 * spelling) without confirming against the mobile source and Mixpanel Lexicon.
 */
export const AnalyticsEvent = {
  SIGNUP_SUCCESS: 'Signup Success',
  LOGIN_SUCCESS: 'Login Success',
  MESSAGE_SENT: 'Message Sent',
  CADDIE_ADVICE_CLICKED: 'Caddie Advice Clicked',
  PLAY_GOLF_COURSE_STARTED: 'Play Golf Cousre started',
  END_ROUND: 'End Round',
  TEE_TIME_SEARCH: 'Tee Time Search',
  TEE_TIME_SELECTED: 'Tee Time Selected',
  BOOKING_CONFIRMED: 'Booking Confirmed',
} as const;

export type AnalyticsEventName =
  (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

interface MixpanelApi {
  track: (event: string, properties?: Record<string, unknown>) => void;
}

interface MixpanelWindow {
  mixpanel?: MixpanelApi;
  __DISABLE_MIXPANEL__?: boolean;
}

/**
 * Thin wrapper over the global window.mixpanel.track, mirroring the mobile
 * app's mixpanelService. All calls are no-ops during SSR, in embedded
 * contexts, when Mixpanel is disabled, or if the snippet failed to load.
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly platformId = inject<object>(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  track(event: AnalyticsEventName, properties?: Record<string, unknown>): void {
    if (!this.isBrowser) return;

    try {
      const w = window as unknown as MixpanelWindow;
      if (w.__DISABLE_MIXPANEL__) return;
      if (this.isEmbedded()) return;
      if (!w.mixpanel || typeof w.mixpanel.track !== 'function') return;

      w.mixpanel.track(event, {
        platform: 'web',
        ...properties,
      });
    } catch (_) {
      // Analytics must never break the app.
    }
  }

  private isEmbedded(): boolean {
    try {
      return window.location.pathname.includes('/embed/');
    } catch (_) {
      return false;
    }
  }
}
